import { z } from "zod";
import type {
  LearnerFormCustomField,
  LearnerFormResponse,
  LearnerFormStandardField,
} from "@eduviaos/shared";

/**
 * Phase SC-22 — client-side validation built from the tenant's resolved
 * learner form. Mirrors the backend rules in
 * `students/learner_form_enforcement.py` so the operator sees the error
 * next to the field instead of a 422 banner.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TEXT_MAX = 500;

/** Fields the backend fills when left empty, so "required" never blocks. */
const AUTO_FILLED = new Set(["admission_date", "primary_language"]);

const MAX_LENGTH: Record<string, number> = {
  name: 120,
  phone: 20,
  roll_number: 40,
  admission_number: 40,
  grade: 20,
  section: 20,
  blood_group: 8,
  aadhaar_reference: 80,
  parent_name: 120,
};

export type LearnerFormErrors = Record<string, string>;

function phoneDigitsOk(value: string): boolean {
  const digits = value.replace(/\D/g, "");
  return digits.length >= 7 && digits.length <= 16;
}

function validDate(value: string): boolean {
  if (!DATE_RE.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().startsWith(value);
}

function standardFieldSchema(field: LearnerFormStandardField): z.ZodType<string> {
  const max = MAX_LENGTH[field.key] ?? TEXT_MAX;
  const mustFill = field.required && !AUTO_FILLED.has(field.key);
  return z.string().superRefine((raw, ctx) => {
    const value = raw.trim();
    if (!value) {
      if (mustFill)
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${field.label} is required.` });
      return;
    }
    if (value.length > max) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Keep ${field.label.toLowerCase()} under ${max} characters.`,
      });
      return;
    }
    if (field.kind === "email" && !EMAIL_RE.test(value)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Enter a valid email address." });
    } else if (field.kind === "phone" && !phoneDigitsOk(value)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Enter a valid phone number." });
    } else if (field.kind === "date" && !validDate(value)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Pick a valid date." });
    }
  });
}

function customFieldSchema(field: LearnerFormCustomField): z.ZodType<string> {
  return z.string().superRefine((raw, ctx) => {
    const value = raw.trim();
    if (!value) {
      if (field.required)
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${field.label} is required.` });
      return;
    }
    const fail = (message: string) => ctx.addIssue({ code: z.ZodIssueCode.custom, message });
    switch (field.type) {
      case "number":
        if (!Number.isFinite(Number(value))) fail("Enter a number.");
        break;
      case "date":
        if (!validDate(value)) fail("Pick a valid date.");
        break;
      case "select":
        if (!field.options.includes(value)) fail("Pick one of the listed options.");
        break;
      case "phone":
        if (!phoneDigitsOk(value)) fail("Enter a valid phone number.");
        break;
      case "email":
        if (!EMAIL_RE.test(value)) fail("Enter a valid email address.");
        break;
      case "text":
        if (value.length > TEXT_MAX) fail(`Keep it under ${TEXT_MAX} characters.`);
        break;
    }
  });
}

/** Key used for a custom field in the draft + error maps. */
export function customDraftKey(key: string): string {
  return `custom.${key}`;
}

/**
 * Validates the draft against the visible fields of `form`. Hidden fields
 * are never validated (the backend ignores them). Returns an empty object
 * when the draft is valid.
 */
export function validateLearnerDraft(
  form: LearnerFormResponse,
  values: Record<string, string>,
  custom: Record<string, string>,
  options: { readOnlyKeys?: ReadonlySet<string> } = {},
): LearnerFormErrors {
  const shape: Record<string, z.ZodType<string>> = {};
  const input: Record<string, string> = {};
  for (const field of form.fields) {
    if (!field.visible || options.readOnlyKeys?.has(field.key)) continue;
    shape[field.key] = standardFieldSchema(field);
    input[field.key] = values[field.key] ?? "";
  }
  for (const field of form.custom_fields) {
    if (!field.visible) continue;
    const key = customDraftKey(field.key);
    shape[key] = customFieldSchema(field);
    input[key] = custom[field.key] ?? "";
  }
  const result = z.object(shape).safeParse(input);
  if (result.success) return {};
  const errors: LearnerFormErrors = {};
  for (const issue of result.error.issues) {
    const key = issue.path.join(".");
    if (!(key in errors)) errors[key] = issue.message;
  }
  return errors;
}
